import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { RouteStep } from "../navigation/types";
import {
  type DirectionsRoute,
  FormSelectorMode,
  RouteComputationStatus,
  type RouteType,
  type SliceProps,
} from "./types";

const initialState: SliceProps = {
  errors: [],
  isAutoPlaying: true,
  routeComputationStatus: RouteComputationStatus.FINISHED,
  routes: [],
  search: {
    enabled: false,
    selectorNameSelected: FormSelectorMode.null,
    showingMap: true,
  },
  selectedRouteId: null,
  selectedRouteType: null,
};

const DirectionsSlice = createSlice({
  initialState,
  name: "directionsSlice",
  reducers: {
    clearDirectionsRoutes: (
      state: SliceProps,
      _action: PayloadAction<void>,
    ) => {
      state.routes = [];
      state.routeComputationStatus = RouteComputationStatus.FINISHED;
      state.errors = initialState.errors;
    },
    pushRoute: (state: SliceProps, action: PayloadAction<DirectionsRoute>) => {
      // Set the current step for the route
      if (action.payload.steps) {
        action.payload.currentStep = action.payload.steps[0];
      }
      state.routes?.push(action.payload);
    },
    resetCurrentStep: (
      state: SliceProps,
      action: PayloadAction<DirectionsRoute["id"]>,
    ) => {
      const route = state.routes?.find((s) => s.id === action.payload);
      if (route) {
        route.currentStep = route.steps[0];
      }
    },
    resetCurrentStepsToFloor: (
      state: SliceProps,
      action: PayloadAction<number>,
    ) => {
      state.routes?.forEach((r) => {
        const currentStep = r?.steps.find(
          (s) => parseInt(s.from.floorIdentifier) == action.payload,
        );
        r.currentStep = currentStep;
      });
    },
    setCurrentStepById: (
      state: SliceProps,
      action: PayloadAction<RouteStep["id"]>,
    ) => {
      const currentRoute = state.routes.find(
        (r) => r.id === state.selectedRouteId,
      );
      if (currentRoute && action.payload) {
        const nextCurrentStep = currentRoute.steps.find(
          (step) => step.id == action.payload,
        );
        if (nextCurrentStep) {
          currentRoute.currentStep = nextCurrentStep;
        }
      }
    },
    setDirectionsErrors: (
      state: SliceProps,
      action: PayloadAction<SliceProps["errors"]>,
    ) => {
      state.errors = action.payload;
    },
    setDirectionsSearchModeEnabled: (
      state: SliceProps,
      action: PayloadAction<boolean>,
    ) => {
      state.search.enabled = action.payload;
    },
    setDirectionsSelectorNameSelected: (
      state: SliceProps,
      action: PayloadAction<FormSelectorMode>,
    ) => {
      state.search.selectorNameSelected = action.payload;
    },
    setDirectionsShowingMap: (
      state: SliceProps,
      action: PayloadAction<boolean>,
    ) => {
      state.search.showingMap = action.payload;
    },
    setIsAutoPlaying: (
      state: SliceProps,
      action: PayloadAction<SliceProps["isAutoPlaying"]>,
    ) => {
      state.isAutoPlaying = action.payload;
    },
    setRouteComputationStatus: (
      state: SliceProps,
      action: PayloadAction<SliceProps["routeComputationStatus"]>,
    ) => {
      state.routeComputationStatus = action.payload;
    },
    setSelectedRouteId: (
      state: SliceProps,
      action: PayloadAction<SliceProps["selectedRouteId"]>,
    ) => {
      state.selectedRouteId = action.payload;
    },
    setSelectedRouteType: (
      state: SliceProps,
      action: PayloadAction<SliceProps["selectedRouteType"]>,
    ) => {
      state.selectedRouteType = action.payload;
    },
    updateCurrentStep: (
      state: SliceProps,
      action: PayloadAction<{
        routeId: DirectionsRoute["id"];
        currentStep: DirectionsRoute["currentStep"];
      }>,
    ) => {
      const route = state.routes?.find((s) => s.id === action.payload.routeId);
      if (route) {
        route.currentStep = action.payload.currentStep;
      }
    },
    updateRouteTags: (
      state: SliceProps,
      action: PayloadAction<{
        routeId: number;
        tags: RouteType[];
      }>,
    ) => {
      const route = state.routes?.find((r) => r.id === action.payload.routeId);
      if (route) {
        route.tags = [...new Set([...route.tags, ...action.payload.tags])];
      }
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  clearDirectionsRoutes,
  pushRoute,
  updateCurrentStep,
  resetCurrentStep,
  setCurrentStepById,
  resetCurrentStepsToFloor,
  setRouteComputationStatus,
  setSelectedRouteId,
  setSelectedRouteType,
  updateRouteTags,
  setDirectionsShowingMap,
  setIsAutoPlaying,
  setDirectionsSearchModeEnabled,
  setDirectionsSelectorNameSelected,
  setDirectionsErrors,
} = DirectionsSlice.actions;

export default DirectionsSlice.reducer;
